import React, { useEffect, useState } from "react";
import { Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton } from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { LibraryPage } from "./LibraryPage";
import { FocusPage, EditingFocus } from "./FocusPage";
import { FocusDTO, FocusValidationConflictError, getFocusLibrary } from "@/js/api/focus";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { SelectStudentsPage } from "./SelectStudentsPage";
import ErrorBoundary from "@/components/ErrorBoundary";
import { UNEXPECTED_ERROR } from "@/utils/envUtils";

export enum LibraryDrawerView {
	/**
	 * This value refers to the view where the teacher is browsing the focus library.
	 */
	LibraryView,
	/**
	 * This value refers to the view where the teacher is creating or editing a focus.
	 */
	FocusView,
	/**
	 * This value refers to the view where the teacher is selecting which students to apply the focus to.
	 * It is only visible when applying a focus to a class. Not when creating or editing one directly from My Classes page.
	 */
	SelectStudents,
}

interface Props {
	isOpen: boolean;
	closeDrawer: () => void;
	/**
	 * `classIsActive` is only required for passing to the `FocusPage` component.
	 */
	classIsActive?: boolean;
	/**
	 * `focus` is only used in the AppliedFocus.tsx component. So this can also be used to determine whether or not the drawer was opened from that edit button.
	 */
	focus?: EditingFocus;
	bubbleAppliedOrUpdatedFocus?: (appliedFocus: FocusDTO) => void;
}

export const FocusLibraryDrawer = (props: Props): JSX.Element => {
	/**
	 * Which view to jump to when the drawer is first opened.
	 */
	const defaultView = props.focus ? LibraryDrawerView.FocusView : LibraryDrawerView.LibraryView;

	const userSessionStore = useUserSessionStore();
	const focusStore = useFocusStore();
	const [hasFetchedLibrary, setHasFetchedLibrary] = useState(false);

	/**
	 * The focus being edited or applied.
	 */
	const [focus, setFocus] = useState<EditingFocus | undefined>(props.focus);

	const [view, setView] = useState<LibraryDrawerView>(defaultView);
	const [validationError, setValidationError] = useState<FocusValidationConflictError | undefined>(undefined);

	const { errorToast } = useToast();

	function jumpToView(view: LibraryDrawerView) {
		if (view === LibraryDrawerView.LibraryView) {
			setFocus(undefined);
			setValidationError(undefined);
		}
		setView(view);
	}

	function handleCloseDrawer() {
		props.closeDrawer();
		focusStore.setOpenedDirectlyToEdit(false);
		setView(defaultView);

		setFocus(undefined);
		setValidationError(undefined);
	}

	// When it is first opened, reset the view
	useEffect(() => {
		if (props.isOpen) {
			setView(defaultView);
			setFocus(props.focus);
		}
	}, [props.isOpen, props.focus, defaultView]);

	/**
	 * A reusable function to handle the error thrown when creating or updating a focus.
	 */
	function handleUpsertFailure(error: Error) {
		if (!(error instanceof FocusValidationConflictError)) {
			errorToast({
				title: "Failed to save the Focus.",
				description: UNEXPECTED_ERROR,
				isClosable: true,
			});
			return;
		}

		setValidationError(error);

		if (error.duplicatedName) {
			errorToast({
				title: "Focus name already exists",
				description: "Please ensure the focus name is unique.",
				isClosable: true,
			});
			return;
		}

		if (
			(error.conflictingFocusTabSignatures && error.conflictingFocusTabSignatures.length > 0) ||
			(error.conflictingFocusTabUrls && error.conflictingFocusTabUrls.length > 0)
		) {
			errorToast({
				title: "Focus validation against School Manager Locked rules failed",
				description: "Please address remove the highlighted resource(s) to continue.",
				isClosable: true,
			});
		}
	}

	// When the drawer is opened, ensure focusStore is up to date.
	// This safe guards against teachers having multiple tabs open making state invalid in other tabs.
	useEffect(() => {
		if (hasFetchedLibrary) return;

		getFocusLibrary(userSessionStore.getTeacherId() as string)
			.then((response) => {
				focusStore.setFocusLibraryItems(response.focusLibrary);
				focusStore.setFetchingLibraryItems(false);
			})
			.catch((error) => {
				console.error("Failed to get Focus Library", error);
				focusStore.setFetchingLibraryItems(false);
				errorToast({
					title: "Failed to get Focus Library",
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			})
			.finally(() => {
				setHasFetchedLibrary(true);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorToast, focusStore, hasFetchedLibrary]);

	function renderView() {
		switch (view) {
			case LibraryDrawerView.FocusView:
				return (
					<FocusPage
						jumpToView={jumpToView}
						closeDrawer={handleCloseDrawer}
						classIsActive={props.classIsActive}
						bubbleAppliedOrUpdatedFocus={props.bubbleAppliedOrUpdatedFocus}
						openedFromAppliedFocusPill={!!props.focus}
						focusBeingEdited={focus} // provided when opening the drawer to edit an existing focus.
						setEditingFocus={setFocus} // called when creating a new focus and then continuing to Select Students.
						validationError={validationError}
						handleUpsertFailure={handleUpsertFailure}
					/>
				);
			case LibraryDrawerView.SelectStudents:
				if (!focus) {
					throw new Error("SelectStudentsPage requires focus to be set.");
				}
				return (
					<SelectStudentsPage
						jumpToView={jumpToView}
						closeDrawer={handleCloseDrawer}
						classIsActive={props.classIsActive}
						focusToBeApplied={focus}
						bubbleAppliedOrUpdatedFocus={props.bubbleAppliedOrUpdatedFocus}
						handleUpsertFailure={handleUpsertFailure}
					/>
				);
			case LibraryDrawerView.LibraryView:
			default: // Default to LibraryView
				return (
					<LibraryPage
						jumpToView={jumpToView}
						closeDrawer={handleCloseDrawer}
						setEditingFocus={setFocus} // called by clicking on an item in the library.
					/>
				);
		}
	}

	return (
		<Drawer isOpen={props.isOpen} onClose={handleCloseDrawer} size={"md"}>
			<DrawerOverlay />
			<DrawerContent>
				<DrawerCloseButton />
				<ErrorBoundary>{renderView()}</ErrorBoundary>
			</DrawerContent>
		</Drawer>
	);
};
